
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, ChevronRight, Clipboard, Plus, Info } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { DatePicker } from '@/components/DatePicker';
import { useAuth } from "@/contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface TaskSuggestion {
  id: string;
  task_name: string;
  descripcion?: string;
  client_organization: string;
  suggested_date: string;
  work_log_id: string;
  created_at: string;
  is_processed: boolean;
  visibility: string;
}

interface OrgUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

const TaskSuggestionsSection: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [currentSuggestion, setCurrentSuggestion] = useState<TaskSuggestion | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const queryClient = useQueryClient();
  const userOrg = user?.organization_name || '';
  const isRevenueHub = userOrg === 'Revenue Hub';
  const firstRender = useRef(true);

  console.log('TaskSuggestionsSection - User organization:', userOrg);

  // Fetch task suggestions specific to the user's organization and visibility
  const { data: suggestions = [], isLoading, refetch } = useQuery({
    queryKey: ['taskSuggestions', userOrg],
    queryFn: async () => {
      if (!user?.organization_name) return [];
      
      console.log('Fetching task suggestions for organization:', user.organization_name);
      
      // FIXED: Different queries based on organization type
      let query;
      if (user.organization_name === 'Revenue Hub') {
        // Revenue Hub users should see tasks with "Revenue Hub" visibility
        query = supabase
          .from('tasks_suggestions')
          .select('*')
          .eq('visibility', 'Revenue Hub')
          .order('suggested_date', { ascending: true });
      } else {
        // Client users should see tasks for their organization with "Client" visibility
        query = supabase
          .from('tasks_suggestions')
          .select('*')
          .eq('client_organization', user.organization_name)
          .eq('visibility', 'Client')
          .order('suggested_date', { ascending: true });
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching task suggestions:', error);
        throw error;
      }
      
      console.log(`Found ${data?.length || 0} task suggestions for ${user.organization_name} with correct visibility`);
      return data as TaskSuggestion[];
    },
    enabled: !!user?.organization_name
  });

  // Set up real-time subscription for task suggestions
  useEffect(() => {
    if (!user?.organization_name) return;
    
    console.log('Setting up real-time subscription for task suggestions', user.organization_name);
    const subscription = supabase
      .channel('tasks_suggestions_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'tasks_suggestions' }, 
        () => {
          console.log('Task suggestions changed, refreshing...');
          refetch();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [refetch, user?.organization_name]);

  // Fetch users from the same organization
  useEffect(() => {
    const fetchOrgUsers = async () => {
      try {
        if (!user?.organization_name) return;
        
        console.log('Fetching users for organization:', user.organization_name);
        
        const { data, error } = await supabase
          .from('profiles')
          .select('id, email, first_name, last_name')
          .eq('organization_name', user.organization_name);
        
        if (error) {
          console.error('Error fetching organization users:', error);
          throw error;
        }
        
        console.log(`Found ${data?.length || 0} users for ${user.organization_name}`);
        setOrgUsers(data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    if (isExpanded && firstRender.current) {
      firstRender.current = false;
      fetchOrgUsers();
    }
  }, [isExpanded, user?.organization_name]);

  // Mutation to create a task when accepting a suggestion
  const createTaskMutation = useMutation({
    mutationFn: async ({ suggestion, userId }: { suggestion: TaskSuggestion, userId: string }) => {
      if (!userId) throw new Error('Debes seleccionar un usuario');
      if (!selectedDate) throw new Error('Debes seleccionar una fecha de vencimiento');
      
      console.log('Creating task from suggestion:', suggestion);
      
      // 1. Get the user's details for the task assignment notification
      const { data: assignedUser, error: userError } = await supabase
        .from('profiles')
        .select('email, first_name, last_name')
        .eq('id', userId)
        .single();
        
      if (userError) throw userError;
      
      // 2. Get the current user's name as the assigner
      const { data: assignerData, error: assignerError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user?.id || '')
        .single();
        
      if (assignerError) throw assignerError;
      
      const assignerName = `${assignerData.first_name || ''} ${assignerData.last_name || ''}`.trim();

      // FIXED: Create the Kanban card with correct organization and user assignments
      // Everyone in the same organization should be able to see this card
      const { data: newCard, error: createError } = await supabase
        .from('kanban_cards')
        .insert({
          title: suggestion.task_name,
          status: 'Por hacer',
          organization_name: userOrg, // Current user's organization
          responsible: assignedUser.first_name || 'Usuario',
          client_name: isRevenueHub ? suggestion.client_organization : 'Revenue Hub',
          priority: 'Alta',
          user_id: user?.id, // Creator is current user
          assigned_to_id: userId, // Assignee is selected user
          position: 999,
          due_date: selectedDate?.toISOString(),
          descripcion: suggestion.descripcion || '' // Add the description to the card
        })
        .select()
        .single();
      
      if (createError) {
        console.error('Error creating kanban card:', createError);
        throw createError;
      }
      
      // 4. Mark the suggestion as processed
      const { error: deleteError } = await supabase
        .from('tasks_suggestions')
        .delete()
        .eq('id', suggestion.id);
      
      if (deleteError) throw deleteError;
      
      // 5. Send notification email about the task assignment
      try {
        const formattedDate = selectedDate ? new Date(selectedDate).toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : 'Sin fecha';
        
        await supabase.functions.invoke('send-notification-task-assigned', {
          body: {
            assignedToEmail: assignedUser.email,
            assignedToFirstName: assignedUser.first_name || 'Usuario',
            assignedBy: assignerName || userOrg,
            taskTitle: suggestion.task_name,
            dueDate: formattedDate,
            misTareasUrl: 'https://app.revenuehublatam.com/workspace/tasks'
          }
        });
        
        console.log('Task assignment notification sent');
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
        // Continue even if notification fails
      }
      
      return newCard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskSuggestions', userOrg] });
      toast({
        title: "Tarea creada",
        description: "La tarea ha sido creada exitosamente.",
      });
      setSelectedUser(null);
      setSelectedDate(undefined);
      setCurrentSuggestion(null);
    },
    onError: (error) => {
      console.error('Error creating task:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudo crear la tarea."
      });
    }
  });

  // Mutation to reject a suggestion
  const rejectSuggestionMutation = useMutation({
    mutationFn: async (suggestionId: string) => {
      console.log('Rejecting suggestion:', suggestionId);
      
      const { error } = await supabase
        .from('tasks_suggestions')
        .delete()
        .eq('id', suggestionId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskSuggestions', userOrg] });
      toast({
        title: "Sugerencia rechazada",
        description: "La sugerencia ha sido eliminada.",
      });
    },
    onError: (error) => {
      console.error('Error rejecting suggestion:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar la sugerencia."
      });
    }
  });

  const handleAccept = (suggestion: TaskSuggestion) => {
    setCurrentSuggestion(suggestion);
    setSelectedUser(null);
    setSelectedDate(undefined);
  };

  const confirmAccept = () => {
    if (!currentSuggestion || !selectedUser) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Debes seleccionar un usuario."
      });
      return;
    }

    if (!selectedDate) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Debes seleccionar una fecha de vencimiento."
      });
      return;
    }
    
    createTaskMutation.mutate({ 
      suggestion: currentSuggestion, 
      userId: selectedUser 
    });
  };

  const handleReject = (suggestionId: string) => {
    rejectSuggestionMutation.mutate(suggestionId);
  };

  const handleViewAll = () => {
    navigate('/workspace/salesops/history');
  };

  const handleCreateNew = () => {
    navigate('/workspace/salesops/history');
  };

  // Only show section if we have the user's organization
  if (!user?.organization_name) {
    return null;
  }

  return (
    <div className="px-4 mb-4 mt-2">
      <div className="mb-2">
        <button 
          className="flex items-center justify-between w-full py-2 px-3 text-sm font-medium hover:bg-gray-100 rounded-md transition-colors text-gray-700"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center">
            <Clipboard className="h-5 w-5 mr-3 text-gray-500" />
            <span>Tareas Sugeridas</span>
          </div>
          <div className="flex items-center">
            {suggestions.length > 0 && (
              <Badge variant="primary" className="mr-2 text-xs">
                {suggestions.length}
              </Badge>
            )}
            {isExpanded ? (
              <ChevronRight className="h-4 w-4 transform rotate-90" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        </button>
      </div>
      
      {isExpanded && (
        <div className="mb-3 px-1">
          {isLoading ? (
            <div className="flex justify-center py-3">
              <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-3 text-sm text-gray-500">
              <p className="mb-2">No hay sugerencias disponibles.</p>
              {isRevenueHub && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-xs h-7" 
                  onClick={handleCreateNew}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Ver historial de reuniones
                </Button>
              )}
            </div>
          ) : (
            <>
              <ScrollArea className="h-[360px] pr-4" style={{ scrollbarWidth: 'thin' }}>
                <div className="space-y-3">
                  {suggestions.map(suggestion => (
                    <div 
                      key={suggestion.id} 
                      className={`border rounded-md p-3 bg-white shadow-sm hover:shadow-md transition-shadow ${currentSuggestion?.id === suggestion.id ? 'border-primary' : ''}`}
                    >
                      {currentSuggestion?.id === suggestion.id ? (
                        <>
                          <div className="mb-2">
                            <h3 className="font-medium text-gray-900">{suggestion.task_name}</h3>
                            {suggestion.descripcion && (
                              <p className="text-xs text-gray-600 mt-1">{suggestion.descripcion}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {isRevenueHub ? 'Cliente' : 'Para'}: <span className="font-medium">{suggestion.client_organization}</span>
                            </p>
                          </div>
                          <div className="pt-2 border-t border-gray-100">
                            <div className="flex flex-col gap-2">
                              <Select value={selectedUser || ''} onValueChange={setSelectedUser}>
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Asignar a" />
                                </SelectTrigger>
                                <SelectContent>
                                  {orgUsers.map(orgUser => (
                                    <SelectItem key={orgUser.id} value={orgUser.id} className="text-xs">
                                      {orgUser.first_name} {orgUser.last_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              <div className="w-full">
                                <DatePicker
                                  date={selectedDate}
                                  onDateChange={setSelectedDate}
                                  className="h-7 text-xs"
                                  placeholder="Fecha de vencimiento"
                                  allowPastDates={false}
                                />
                              </div>

                              <div className="flex gap-1">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setCurrentSuggestion(null);
                                    setSelectedDate(undefined);
                                  }}
                                  className="h-7 flex-1 text-xs"
                                >
                                  Cancelar
                                </Button>
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={confirmAccept}
                                  disabled={!selectedUser || !selectedDate}
                                  className="h-7 flex-1 text-xs"
                                >
                                  Crear
                                </Button>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-start">
                          <div className="flex-1 min-w-0 pr-2">
                            <div className="flex items-center gap-1">
                              <h3 className="font-medium text-gray-900 text-xs leading-tight">{suggestion.task_name}</h3>
                              {suggestion.descripcion && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p>{suggestion.descripcion}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {suggestion.descripcion && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">{suggestion.descripcion}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-2 mt-1">
                              {isRevenueHub && (
                                <p className="text-xs text-gray-500 truncate">
                                  <span className="font-medium">{suggestion.client_organization}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 shrink-0">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleReject(suggestion.id)}
                              className="h-6 w-6 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                            >
                              <X className="h-3.5 w-3.5" />
                              <span className="sr-only">Rechazar</span>
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleAccept(suggestion)}
                              className="h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-50"
                            >
                              <Check className="h-3.5 w-3.5" />
                              <span className="sr-only">Aceptar</span>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              {isRevenueHub && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-xs h-8 mt-2" 
                  onClick={handleViewAll}
                >
                  Ver todas
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskSuggestionsSection;
